import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import {
  H2,
  H4,
  P,
  Modal,
  ModalHeader,
  ModalBody,
  Icon,
  Grid,
  Header,
  FlexRow,
  BaseCol,
  Subtext,
  ProgressBar
} from '@contently/toolkit';
import styled from '@emotion/styled';
import CalculationBox from './calculation_box';

function RoiWrapper({ appData }) {
  const { response } = JSON.parse(appData);
  const [modal, setModal] = useState(false);

  const {
    pub_id: pubId,
    completed_pubs: completedPubs,
    published_pubs: publishedPubs,
    total_organic_search_traffic: totalOrganicSearchTraffic,
    total_organic_search_traffic_value: totalOrganicSearchTrafficValue,
    total_content_debit: totalContentDebit,
    average_cpc: averageCpc
  } = response;

  const percentPublished = (publishedPubs / completedPubs) * 100;

  const GrayBorderDiv = styled('div')`
    border: 1px solid #d4d4d4;
    padding: 24px 20px;
    margin: 0 18px 0 0;
    height: 100%;
    @media screen and (max-width: 768px) {
      margin-right: 0;
    }
  `;

  const ShadedGrayBorderDiv = styled(GrayBorderDiv)`
    background-color: #f8f8f8;
    flex-grow: 1;
  `;

  const BaseColSpace = styled(BaseCol)`
    margin-bottom: 20px;
  `;

  const DarkGrayBorderDiv = styled(GrayBorderDiv)`
    border: 1px solid #999;
  `;

  const ProgressDiv = styled(GrayBorderDiv)`
    border-color: #efefef;
    background-color: #fcfcfc;
  `;

  const SectionTitle = styled('div')`
    margin: 20px 0 14px 0;
  `;

  const TimeframeDiv = styled('div')`
    text-align: right;
    margin: 0 18px 15px 0;
    @media screen and (max-width: 768px) {
      text-align: left;
    }
  `;

  const incomplete =
    publishedPubs / completedPubs !== 1 ? (
      <FlexRow>
        <BaseColSpace>
          <ProgressDiv>
            <H4>Improve your ROI calculations</H4>
            <ProgressBar percent={percentPublished} />
            <P style={{ marginTop: '10px', fontSize: '16px' }}>
              {publishedPubs.toLocaleString()} published stories are included in your ROI
              calculation, but you have {completedPubs.toLocaleString()} stories completed. Mark{' '}
              <a
                className="roi-link"
                href={`https://contently.com/stories/library?hide_filters=false&publications%5B%5D=${pubId}&published=none&statuses%5B%5D=completed`}
              >
                these stories
              </a>{' '}
              published and add URLs to ensure they're included next month
            </P>
          </ProgressDiv>
        </BaseColSpace>
      </FlexRow>
    ) : (
      ''
    );

  const getDate = () => {
    const date = new Date();
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const dateTimeFormat = new Intl.DateTimeFormat('en', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
    const [{ value: month }, , { value: day }, , { value: year }] = dateTimeFormat.formatToParts(
      lastDay
    );

    return `${month} ${day}, ${year}`;
  };

  const removeCentsAddCommas = arg => {
    return Number(parseFloat(arg).toFixed(0)).toLocaleString('en', {
      minimumFractionDigits: 0
    });
  };

  return (
    <Fragment>
      <Modal isOpen={modal} onClose={() => setModal(false)}>
        <ModalHeader>
          <H2>Content ROI </H2>
        </ModalHeader>
        <ModalBody>
          <P>
            This dashboard measures how much value your published content has generated to date. ROI
            is calculated by subtracting spend on content from traffic value. Values are updated
            every 30 days. To learn more about how we calculate ROI, visit this article in
            Contently's help center.
          </P>
        </ModalBody>
      </Modal>
      <Grid>
        <FlexRow>
          <BaseCol md={6}>
            <Header>
              <H2>
                Content ROI
                <Icon type="question-circle" onClick={() => setModal(true)} />
              </H2>
            </Header>
          </BaseCol>
          <BaseCol md={6}>
            <TimeframeDiv>
              <P style={{ fontSize: '12px' }}>
                All time: Aug 1, 2019 - {getDate()}
                <br />
                Data on this page is automatically updated at the end of each month.
              </P>
            </TimeframeDiv>
          </BaseCol>
        </FlexRow>
        {incomplete}
        <FlexRow>
          <BaseCol>
            <SectionTitle>
              <H4>ROI calculations</H4>
            </SectionTitle>
          </BaseCol>
        </FlexRow>
        <FlexRow>
          <BaseColSpace>
            <DarkGrayBorderDiv>
              <CalculationBox
                id="total-organic-search-roi"
                title={'TOTAL ORGANIC SEARCH ROI'}
                subtext={
                  'Organic Search ROI is calculated by subtracting your total content to date from your total traffic value'
                }
                value={`$${removeCentsAddCommas(
                  totalOrganicSearchTrafficValue - totalContentDebit
                )}`}
              />
            </DarkGrayBorderDiv>
          </BaseColSpace>
        </FlexRow>
        <FlexRow>
          <BaseColSpace>
            <ShadedGrayBorderDiv>
              <P style={{ fontSize: '16px' }}>
                <Icon type="lightbulb" /> Interested in improving your results and learning more?{' '}
                <a
                  className="roi-link"
                  href="https://help.contently.com/en/articles/4273934-understanding-and-improving-your-roi-dashboard"
                >
                  Read our guide
                </a>
                .
              </P>
            </ShadedGrayBorderDiv>
          </BaseColSpace>
        </FlexRow>
        <FlexRow>
          <BaseColSpace md={6}>
            <GrayBorderDiv>
              <CalculationBox
                id="total-traffic-value"
                title={'TOTAL TRAFFIC VALUE'}
                subtext={
                  <Subtext style={{ lineHeight: '18px' }}>
                    Total traffic value details what it would cost to drive an equal amount of{' '}
                    <i>total traffic</i> to your content with Google AdWords for your{' '}
                    <i>ranking keywords</i>.
                  </Subtext>
                }
                value={`$${removeCentsAddCommas(totalOrganicSearchTrafficValue)}`}
              />
            </GrayBorderDiv>
          </BaseColSpace>
          <BaseColSpace md={6}>
            <GrayBorderDiv>
              <CalculationBox
                id="total-content-spend"
                title={'TOTAL CONTENT SPEND'}
                subtext={
                  <Subtext style={{ lineHeight: '18px' }}>
                    The total amount of budget you've spent on content for the stories that
                    attribute to your ROI calculation. See that list of stories{' '}
                    <a
                      className="roi-link"
                      href={`https://contently.com/stories/library?hide_filters=false&publications%5B%5D=${pubId}&published=only&statuses%5B%5D=completed`}
                    >
                      here
                    </a>
                    .
                  </Subtext>
                }
                value={`$${totalContentDebit.toLocaleString()}`}
              />
            </GrayBorderDiv>
          </BaseColSpace>
        </FlexRow>
        <FlexRow>
          <BaseCol>
            <SectionTitle>
              <H4>Traffic value calculations</H4>
            </SectionTitle>
          </BaseCol>
        </FlexRow>
        <FlexRow>
          <BaseColSpace md={6} xl={3}>
            <GrayBorderDiv>
              <CalculationBox
                id="average-cost-per-click"
                title={'AVERAGE COST PER CLICK'}
                subtext={`The average cost-per-click associated with the keywords that your content ranks for organically. Highly coveted keywords are associated with a higher CPC on Google Ads.`}
                value={`$${averageCpc.toFixed(2).toLocaleString()}`}
              />
            </GrayBorderDiv>
          </BaseColSpace>
          <BaseColSpace md={6} xl={3}>
            <GrayBorderDiv>
              <CalculationBox
                id="total-traffic"
                title={'TOTAL TRAFFIC'}
                subtext={`This is the number of visitors coming to you from organic search.`}
                value={totalOrganicSearchTraffic.toLocaleString()}
              />
            </GrayBorderDiv>
          </BaseColSpace>
        </FlexRow>
      </Grid>
    </Fragment>
  );
}

export default RoiWrapper;

RoiWrapper.propTypes = {
  appData: PropTypes.string.isRequired
};



// WEBPACK FOOTER //
// ./app/javascript/app/components/roi/return_on_investment_wrapper.jsx